import React, { useState } from 'react';
import { AlertFilter, AlertSeverity, AlertType } from '../../types/api';

interface AlertFiltersProps {
  filter: AlertFilter;
  onFilterChange: (filter: AlertFilter) => void;
}

const AlertFilters: React.FC<AlertFiltersProps> = ({ filter, onFilterChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSeverityChange = (severity: AlertSeverity, checked: boolean) => {
    const currentSeverities = filter.severity || [];
    const newSeverities = checked 
      ? [...currentSeverities, severity]
      : currentSeverities.filter(s => s !== severity);
    
    onFilterChange({
      ...filter,
      severity: newSeverities.length > 0 ? newSeverities : undefined
    });
  };

  const handleTypeChange = (type: AlertType, checked: boolean) => {
    const currentTypes = filter.type || [];
    const newTypes = checked 
      ? [...currentTypes, type]
      : currentTypes.filter(t => t !== type);
    
    onFilterChange({
      ...filter,
      type: newTypes.length > 0 ? newTypes : undefined
    });
  };

  const handleResolvedChange = (resolved: boolean | undefined) => {
    onFilterChange({
      ...filter,
      resolved
    });
  };

  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    const date = value ? new Date(value) : undefined;
    const currentRange = filter.dateRange || { start: new Date(), end: new Date() };
    
    onFilterChange({
      ...filter,
      dateRange: {
        ...currentRange,
        [field]: date
      }
    });
  };

  const clearFilters = () => {
    onFilterChange({});
  };

  const hasActiveFilters = Object.keys(filter).some(key => {
    const value = filter[key as keyof AlertFilter];
    return value !== undefined && value !== null;
  });

  const getSeverityIcon = (severity: AlertSeverity) => {
    switch (severity) {
      case AlertSeverity.CRITICAL: return '🚨';
      case AlertSeverity.ERROR: return '❌';
      case AlertSeverity.WARNING: return '⚠️';
      case AlertSeverity.INFO: return 'ℹ️';
      default: return '📋';
    }
  };

  const getTypeLabel = (type: AlertType) => {
    return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Alert Filters</h3>
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear all
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            {isExpanded ? 'Hide filters' : 'Show filters'}
          </button>
        </div>
      </div>

      {/* Quick Filters */}
      <div className="mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Quick filters:</span>
          <button
            onClick={() => handleResolvedChange(false)}
            className={`px-3 py-1 text-xs rounded-full transition-colors ${
              filter.resolved === false 
                ? 'bg-red-100 text-red-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Unresolved only
          </button>
          <button
            onClick={() => onFilterChange({
              ...filter,
              severity: [AlertSeverity.CRITICAL, AlertSeverity.ERROR]
            })}
            className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-full hover:bg-red-200"
          >
            Critical & Error
          </button>
          <button
            onClick={() => onFilterChange({
              ...filter,
              dateRange: {
                start: new Date(Date.now() - 24 * 60 * 60 * 1000),
                end: new Date()
              }
            })}
            className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200"
          >
            Last 24h
          </button>
          <button
            onClick={() => onFilterChange({
              ...filter,
              type: [AlertType.VALIDATOR_OFFLINE, AlertType.PENDING_TIMEOUT]
            })}
            className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full hover:bg-yellow-200"
          >
            System issues
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Severity Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Severity Level</label>
            <div className="space-y-2">
              {Object.values(AlertSeverity).map((severity) => (
                <label key={severity} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filter.severity?.includes(severity) || false}
                    onChange={(e) => handleSeverityChange(severity, e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 flex items-center">
                    <span className="mr-1">{getSeverityIcon(severity)}</span>
                    {severity.charAt(0).toUpperCase() + severity.slice(1)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Alert Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Alert Type</label>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {Object.values(AlertType).map((type) => (
                <label key={type} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filter.type?.includes(type) || false}
                    onChange={(e) => handleTypeChange(type, e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {getTypeLabel(type)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Status & Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="space-y-2 mb-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="resolvedStatus"
                  checked={filter.resolved === undefined}
                  onChange={() => handleResolvedChange(undefined)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">All alerts</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="resolvedStatus"
                  checked={filter.resolved === false}
                  onChange={() => handleResolvedChange(false)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Unresolved only</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="resolvedStatus"
                  checked={filter.resolved === true}
                  onChange={() => handleResolvedChange(true)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Resolved only</span>
              </label>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <div className="space-y-2">
              <input
                type="datetime-local"
                placeholder="Start date"
                value={filter.dateRange?.start ? filter.dateRange.start.toISOString().slice(0, 16) : ''}
                onChange={(e) => handleDateRangeChange('start', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <input
                type="datetime-local"
                placeholder="End date"
                value={filter.dateRange?.end ? filter.dateRange.end.toISOString().slice(0, 16) : ''}
                onChange={(e) => handleDateRangeChange('end', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertFilters; 